import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios, { AxiosError } from 'axios';
import {
  Receipt,
  Search,
  Filter,
  RefreshCw,
  Plus,
  Edit3,
  History,
  Calculator,
  ShieldCheck,
  CheckCircle2,
  AlertTriangle,
  X,
  ArrowRight,
  TrendingUp,
  Percent,
  Layers,
  Calendar,
  User,
  Clock,
  ChevronRight,
  Sparkles,
} from 'lucide-react';
import { formatZAR, formatPercent } from '../../utils/format';
import { useAuth } from '../../contexts/AuthContext';

function localDateIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const FEE_SCHEDULE_REASON_MAX = 2000;

interface VolumeBand {
  minTxns: number;
  maxTxns: number | null;
  supplierCostIncl: number;
  mmMarkupIncl: number | null;
}

function readVolumeBands(metadata: Record<string, unknown> | null | undefined): VolumeBand[] | null {
  if (!metadata || typeof metadata !== 'object') return null;
  const tiers = metadata.tiers;
  if (!Array.isArray(tiers) || tiers.length === 0) return null;
  const bands: VolumeBand[] = [];
  for (const raw of tiers) {
    if (!raw || typeof raw !== 'object') continue;
    const band = raw as Record<string, unknown>;
    const minTxns = Number(band.minTxns);
    const supplierRaw = band.supplierCostIncl ?? band.feeVatIncl;
    const supplierCostIncl = Number(supplierRaw);
    if (!Number.isFinite(minTxns) || !Number.isFinite(supplierCostIncl)) continue;
    const maxRaw = band.maxTxns;
    const maxNum = maxRaw == null || maxRaw === '' ? null : Number(maxRaw);
    const markupRaw = band.mmMarkupIncl;
    const markupNum = markupRaw == null || markupRaw === '' ? null : Number(markupRaw);
    bands.push({
      minTxns,
      maxTxns: maxNum != null && Number.isFinite(maxNum) ? maxNum : null,
      supplierCostIncl,
      mmMarkupIncl: markupNum != null && Number.isFinite(markupNum) ? markupNum : null,
    });
  }
  return bands.length ? bands : null;
}

function flatCustomerFeeVatIncl(metadata: Record<string, unknown> | null | undefined): number | null {
  if (!metadata) return null;
  const amount = Number(metadata.customerFeeVatIncl);
  return Number.isFinite(amount) && amount > 0 ? amount : null;
}

function feeHasVolumeBands(fee: { metadata?: Record<string, unknown> | null } | null | undefined): boolean {
  return readVolumeBands(fee?.metadata) != null;
}

function scheduleLocksVolumeEdit(fee: FeeScheduleItem | null): boolean {
  if (!fee) return false;
  return feeHasVolumeBands(fee) || feeHasVolumeBands(fee.upcoming);
}

function formatCountRange(band: VolumeBand): string {
  const min = band.minTxns.toLocaleString('en-ZA');
  if (band.maxTxns == null) return `${min}+`;
  return `${min}–${band.maxTxns.toLocaleString('en-ZA')}`;
}

function bandMarkup(band: VolumeBand, flat: number | null): number {
  if (band.mmMarkupIncl != null) return band.mmMarkupIncl;
  if (flat != null) return Math.max(0, Number((flat - band.supplierCostIncl).toFixed(2)));
  return 0;
}

function bandCustomerFee(band: VolumeBand, flat: number | null): number {
  if (flat != null) return flat;
  return Number((band.supplierCostIncl + bandMarkup(band, null)).toFixed(2));
}

const VolumeBandPanel: React.FC<{ fee: FeeScheduleItem; heading: string }> = ({ fee, heading }) => {
  const bands = readVolumeBands(fee.metadata);
  if (!bands) return null;
  const flat = flatCustomerFeeVatIncl(fee.metadata);
  const windowLabel = fee.effectiveTo
    ? `${fee.effectiveFrom} to ${fee.effectiveTo}`
    : `from ${fee.effectiveFrom}`;

  return (
    <div className="rounded-xl border border-[var(--border)] bg-[var(--muted)]/20 p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-xs font-bold uppercase tracking-wider text-[var(--foreground)]">{heading}</h3>
        <span className="text-[11px] text-[var(--muted-foreground)]">{windowLabel}</span>
      </div>
      {flat != null ? (
        <p className="text-xs text-[var(--muted-foreground)]">
          Customer fee is a flat {formatZAR(flat)} VAT incl. The bank cost is the band for this month&apos;s successful count. Markup is the difference.
        </p>
      ) : (
        <p className="text-xs text-[var(--muted-foreground)]">
          The customer pays the bank cost plus the markup on the band. The band follows this month&apos;s successful count.
        </p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-[var(--muted-foreground)]">
              <th className="py-1.5 pr-3 font-semibold">Successful count</th>
              <th className="py-1.5 pr-3 font-semibold text-right">Bank cost</th>
              <th className="py-1.5 pr-3 font-semibold text-right">Markup</th>
              <th className="py-1.5 font-semibold text-right">Customer fee</th>
            </tr>
          </thead>
          <tbody>
            {bands.map((band) => (
              <tr key={`${band.minTxns}-${band.maxTxns ?? 'open'}`} className="border-t border-[var(--border)]">
                <td className="py-1.5 pr-3 text-[var(--foreground)]">{formatCountRange(band)}</td>
                <td className="py-1.5 pr-3 text-right">{formatZAR(band.supplierCostIncl)}</td>
                <td className="py-1.5 pr-3 text-right text-emerald-700">{formatZAR(bandMarkup(band, flat))}</td>
                <td className="py-1.5 text-right font-semibold">{formatZAR(bandCustomerFee(band, flat))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const API = axios.create({ baseURL: '/api/v1/admin' });
API.interceptors.request.use((cfg) => {
  const token = sessionStorage.getItem('portal_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

export interface VatSplit {
  incl: number;
  exVat: number;
  vat: number;
}

export interface FeeScheduleItem {
  id: number;
  feeKey: string;
  supplierCode: string | null;
  description: string | null;
  currency: string;
  vatRate: number;
  feeType: 'fixed' | 'percentage' | 'hybrid' | 'tiered';
  supplierCostIncl: number;
  mmMarkupIncl: number;
  supplierPercentageFee: number;
  mmPercentageFee: number;
  minFeeIncl: number | null;
  maxFeeIncl: number | null;
  tierLevel: string;
  metadata: Record<string, unknown>;
  effectiveFrom: string;
  effectiveTo: string | null;
  isActive: boolean;
  totalIncl: number;
  supplierCost: VatSplit;
  mmMarkup: VatSplit;
  total: VatSplit;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  isLive?: boolean;
  upcoming?: FeeScheduleItem | null;
}

export interface FeeScheduleSummary {
  totalRails: number;
  fixedCount: number;
  percentageCount: number;
  tieredCount: number;
  suppliers: string[];
}

export interface FeeAuditEntry {
  id: number;
  feeScheduleId: number;
  action: string;
  oldValues: Record<string, unknown> | null;
  newValues: Record<string, unknown> | null;
  changedBy: string;
  reason: string | null;
  changedAt: string;
}

export interface HistoryData {
  feeKey: string;
  current: FeeScheduleItem;
  history: FeeScheduleItem[];
  audits: FeeAuditEntry[];
}

export interface SimulationResult {
  principal: number;
  supplierCostIncl: number;
  mmMarkupIncl: number;
  totalFeeIncl: number;
  totalDebit: number;
  supplierCost: VatSplit;
  mmMarkup: VatSplit;
  total: VatSplit;
  netRevenue: number;
  netVatPayable: number;
}

const PRESET_AMOUNTS = [50, 100, 250, 500, 1000, 2500, 5000];

export const FeeScheduleOverlay: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // Table & state
  const [fees, setFees] = useState<FeeScheduleItem[]>([]);
  const [summary, setSummary] = useState<FeeScheduleSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successNotice, setSuccessNotice] = useState<string | null>(null);
  const [drawerError, setDrawerError] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState<{
    message: string;
    feeKey: string;
    effectiveFrom: string;
    liveNow: boolean;
  } | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [selectedFeeType, setSelectedFeeType] = useState('');

  // Edit Drawer
  const [editingFee, setEditingFee] = useState<FeeScheduleItem | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editForm, setEditForm] = useState({
    feeType: 'fixed' as 'fixed' | 'percentage' | 'hybrid' | 'tiered',
    supplierCostIncl: 0,
    mmMarkupIncl: 0,
    supplierPercentageFee: 0,
    mmPercentageFee: 0,
    minFeeIncl: '' as string | number,
    maxFeeIncl: '' as string | number,
    vatRate: 0.15,
    newEffectiveFrom: localDateIso(),
    reason: '',
  });

  // History Drawer
  const [historyFeeKey, setHistoryFeeKey] = useState<string | null>(null);
  const [historyData, setHistoryData] = useState<HistoryData | null>(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Live Simulator Modal
  const [simulatingFee, setSimulatingFee] = useState<FeeScheduleItem | null>(null);
  const [simAmount, setSimAmount] = useState<number>(500);
  const [simResult, setSimResult] = useState<SimulationResult | null>(null);
  const [calculatingSim, setCalculatingSim] = useState(false);

  // Drawer live preview calculation state
  const [drawerSimAmount, setDrawerSimAmount] = useState<number>(500);
  const [drawerSimResult, setDrawerSimResult] = useState<SimulationResult | null>(null);

  const fetchFees = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);

    try {
      const params: Record<string, string> = {};
      if (search) params.search = search;
      if (selectedSupplier) params.supplierCode = selectedSupplier;
      if (selectedFeeType) params.feeType = selectedFeeType;

      const res = await API.get<{ success: boolean; data: { fees: FeeScheduleItem[]; summary: FeeScheduleSummary } }>('/fee-schedules', { params });
      if (res.data.success) {
        setFees(res.data.data.fees);
        setSummary(res.data.data.summary);
      }
    } catch (err) {
      const axiosError = err as AxiosError<{ error?: string }>;
      setError(axiosError.response?.data?.error || 'Failed to load fee schedules');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [search, selectedSupplier, selectedFeeType]);

  useEffect(() => {
    fetchFees();
  }, [fetchFees]);

  // Open Edit Drawer
  const handleOpenEdit = (fee: FeeScheduleItem) => {
    setEditingFee(fee);
    setDrawerError(null);
    setConfirmOpen(false);
    const defaultDate = localDateIso();

    setEditForm({
      feeType: fee.feeType,
      supplierCostIncl: fee.supplierCostIncl,
      mmMarkupIncl: fee.mmMarkupIncl,
      supplierPercentageFee: fee.supplierPercentageFee,
      mmPercentageFee: fee.mmPercentageFee,
      minFeeIncl: fee.minFeeIncl !== null ? fee.minFeeIncl : '',
      maxFeeIncl: fee.maxFeeIncl !== null ? fee.maxFeeIncl : '',
      vatRate: fee.vatRate,
      newEffectiveFrom: defaultDate,
      reason: '',
    });
  };

  // Live calculation inside Edit Drawer
  useEffect(() => {
    if (!editingFee) return;

    const principal = Number(drawerSimAmount) || 0;
    let supplierCost = Number(editForm.supplierCostIncl) || 0;
    let mmMarkup = Number(editForm.mmMarkupIncl) || 0;

    if (editForm.feeType === 'percentage' || editForm.feeType === 'hybrid') {
      supplierCost += principal * (Number(editForm.supplierPercentageFee) || 0);
      mmMarkup += principal * (Number(editForm.mmPercentageFee) || 0);
    }

    let totalFee = supplierCost + mmMarkup;

    if (editForm.minFeeIncl !== '' && editForm.minFeeIncl !== null && editForm.minFeeIncl !== undefined) {
      const minVal = Number(editForm.minFeeIncl);
      if (totalFee < minVal) {
        totalFee = minVal;
        mmMarkup = Math.max(0, totalFee - supplierCost);
      }
    }

    if (editForm.maxFeeIncl !== '' && editForm.maxFeeIncl !== null && editForm.maxFeeIncl !== undefined) {
      const maxVal = Number(editForm.maxFeeIncl);
      if (totalFee > maxVal) {
        totalFee = maxVal;
        mmMarkup = Math.max(0, totalFee - supplierCost);
      }
    }

    const vatParsed = Number(editForm.vatRate);
    const rate = Number.isFinite(vatParsed) ? vatParsed : 0.15;
    const split = (amt: number) => {
      const ex = amt / (1 + rate);
      return { incl: amt, exVat: ex, vat: amt - ex };
    };

    const supplierSplit = split(supplierCost);
    const mmMarkupSplit = split(mmMarkup);
    const totalSplit = split(totalFee);

    setDrawerSimResult({
      principal,
      supplierCostIncl: supplierCost,
      mmMarkupIncl: mmMarkup,
      totalFeeIncl: totalFee,
      totalDebit: principal + totalFee,
      supplierCost: supplierSplit,
      mmMarkup: mmMarkupSplit,
      total: totalSplit,
      netRevenue: mmMarkupSplit.exVat,
      netVatPayable: mmMarkupSplit.vat,
    });
  }, [editingFee, editForm, drawerSimAmount]);

  const handleRequestSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingFee) return;
    const reasonLen = (editForm.reason || '').trim().length;
    if (reasonLen < 5) {
      setDrawerError('A mandatory reason (at least 5 characters) is required for audit compliance.');
      return;
    }
    if (reasonLen > FEE_SCHEDULE_REASON_MAX) {
      setDrawerError(`Reason must be ${FEE_SCHEDULE_REASON_MAX} characters or fewer (${reasonLen} entered).`);
      return;
    }
    if (scheduleLocksVolumeEdit(editingFee)) {
      setDrawerError('Volume-band rails cannot be changed from Fee Schedules. The stored bands stay in force.');
      return;
    }
    setDrawerError(null);
    setConfirmOpen(true);
  };

  const handleConfirmSave = async () => {
    if (!editingFee) return;

    setSavingEdit(true);
    setDrawerError(null);
    setError(null);

    try {
      const vatParsed = Number(editForm.vatRate);
      const payload: Record<string, unknown> = {
        newEffectiveFrom: editForm.newEffectiveFrom,
        reason: editForm.reason.trim(),
        feeType: editForm.feeType,
        supplierCostIncl: Number(editForm.supplierCostIncl) || 0,
        mmMarkupIncl: Number(editForm.mmMarkupIncl) || 0,
        supplierPercentageFee: Number(editForm.supplierPercentageFee) || 0,
        mmPercentageFee: Number(editForm.mmPercentageFee) || 0,
        minFeeIncl: editForm.minFeeIncl !== '' ? Number(editForm.minFeeIncl) : null,
        maxFeeIncl: editForm.maxFeeIncl !== '' ? Number(editForm.maxFeeIncl) : null,
        vatRate: Number.isFinite(vatParsed) ? vatParsed : 0.15,
      };

      const res = await API.put<{ success: boolean; message: string }>(`/fee-schedules/${editingFee.feeKey}`, payload);
      if (res.data.success) {
        const liveNow = editForm.newEffectiveFrom <= localDateIso();
        const message = res.data.message || `Fee schedule for '${editingFee.feeKey}' updated successfully.`;
        setSuccessNotice(message);
        setSaveSuccess({
          message,
          feeKey: editingFee.feeKey,
          effectiveFrom: editForm.newEffectiveFrom,
          liveNow,
        });
        setConfirmOpen(false);
        setEditingFee(null);
        await fetchFees(true);
      }
    } catch (err) {
      const axiosError = err as AxiosError<{ error?: string }>;
      const msg = axiosError.response?.data?.error || 'Failed to update fee schedule';
      setDrawerError(msg);
      setConfirmOpen(false);
    } finally {
      setSavingEdit(false);
    }
  };

  // Open History Drawer
  const handleOpenHistory = async (feeKey: string) => {
    setHistoryFeeKey(feeKey);
    setLoadingHistory(true);
    setHistoryData(null);
    setError(null);

    try {
      const res = await API.get<{ success: boolean; data: HistoryData }>(`/fee-schedules/${feeKey}/history`);
      if (res.data.success) {
        setHistoryData(res.data.data);
      }
    } catch (err) {
      const axiosError = err as AxiosError<{ error?: string }>;
      setError(axiosError.response?.data?.error || 'Failed to fetch history');
    } finally {
      setLoadingHistory(false);
    }
  };

  // Open Standalone Simulator
  const handleOpenSimulate = (fee: FeeScheduleItem) => {
    setSimulatingFee(fee);
    setSimAmount(500);
  };

  // Trigger Standalone Simulation
  const runSimulation = useCallback(async (fee: FeeScheduleItem, amount: number) => {
    setCalculatingSim(true);
    try {
      const res = await API.post<{ success: boolean; data: { calculation: SimulationResult } }>('/fee-schedules/simulate', {
        feeKey: fee.feeKey,
        amount,
      });
      if (res.data.success) {
        setSimResult(res.data.data.calculation);
      }
    } catch {
      // fallback to local calculation
      const rate = Number.isFinite(Number(fee.vatRate)) ? Number(fee.vatRate) : 0.15;
      let supplierCost = Number(fee.supplierCostIncl) || 0;
      let mmMarkup = Number(fee.mmMarkupIncl) || 0;

      if (fee.feeType === 'percentage' || fee.feeType === 'hybrid') {
        supplierCost += amount * (Number(fee.supplierPercentageFee) || 0);
        mmMarkup += amount * (Number(fee.mmPercentageFee) || 0);
      }

      let totalFee = supplierCost + mmMarkup;
      if (fee.minFeeIncl !== null && totalFee < fee.minFeeIncl) {
        totalFee = fee.minFeeIncl;
        mmMarkup = Math.max(0, totalFee - supplierCost);
      }
      if (fee.maxFeeIncl !== null && totalFee > fee.maxFeeIncl) {
        totalFee = fee.maxFeeIncl;
        mmMarkup = Math.max(0, totalFee - supplierCost);
      }

      const split = (amt: number) => ({ incl: amt, exVat: amt / (1 + rate), vat: amt - (amt / (1 + rate)) });
      const supplierSplit = split(supplierCost);
      const mmMarkupSplit = split(mmMarkup);
      const totalSplit = split(totalFee);

      setSimResult({
        principal: amount,
        supplierCostIncl: supplierCost,
        mmMarkupIncl: mmMarkup,
        totalFeeIncl: totalFee,
        totalDebit: amount + totalFee,
        supplierCost: supplierSplit,
        mmMarkup: mmMarkupSplit,
        total: totalSplit,
        netRevenue: mmMarkupSplit.exVat,
        netVatPayable: mmMarkupSplit.vat,
      });
    } finally {
      setCalculatingSim(false);
    }
  }, []);

  useEffect(() => {
    if (simulatingFee) {
      runSimulation(simulatingFee, simAmount);
    }
  }, [simulatingFee, simAmount, runSimulation]);

  const feeModelBadge = (type: string) => {
    switch (type) {
      case 'percentage':
        return <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-semibold text-blue-700 border border-blue-200"><Percent className="w-3 h-3" /> Percentage</span>;
      case 'hybrid':
        return <span className="inline-flex items-center gap-1 rounded-full bg-purple-50 px-2.5 py-0.5 text-xs font-semibold text-purple-700 border border-purple-200"><Layers className="w-3 h-3" /> Hybrid</span>;
      case 'tiered':
        return <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-semibold text-amber-700 border border-amber-200"><TrendingUp className="w-3 h-3" /> Tiered</span>;
      default:
        return <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700 border border-emerald-200"><ShieldCheck className="w-3 h-3" /> Fixed</span>;
    }
  };

  const formatRateSummary = (fee: FeeScheduleItem) => {
    if (fee.feeType === 'tiered' && feeHasVolumeBands(fee)) {
      const flat = flatCustomerFeeVatIncl(fee.metadata);
      return flat != null ? `Flat ${formatZAR(flat)}` : 'Volume bands';
    }
    if (fee.feeType === 'percentage') {
      const totalPct = (fee.supplierPercentageFee + fee.mmPercentageFee) * 100;
      return formatPercent(totalPct);
    }
    if (fee.feeType === 'hybrid') {
      const fixed = formatZAR(fee.totalIncl);
      const totalPct = (fee.supplierPercentageFee + fee.mmPercentageFee) * 100;
      return `${fixed} + ${formatPercent(totalPct)}`;
    }
    return formatZAR(fee.totalIncl);
  };

  const volumeLocked = scheduleLocksVolumeEdit(editingFee);

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight text-[var(--foreground)]">
              Fee Schedules & Rate Governance
            </h1>
            <span className="inline-flex items-center rounded-md bg-[var(--primary)]/10 px-2 py-1 text-xs font-medium text-[var(--primary)] ring-1 ring-inset ring-[var(--primary)]/20">
              Live MMTP Engine
            </span>
          </div>
          <p className="text-sm text-[var(--muted-foreground)] mt-1">
            Manage centralized, effective-dated transaction fees, supplier pass-through costs, MMTP markups, and output VAT calculations.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchFees(true)}
            disabled={refreshing || loading}
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--border)] bg-[var(--card)] px-3 py-2 text-sm font-medium text-[var(--foreground)] hover:bg-[var(--muted)] transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin text-[var(--primary)]' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Alerts / Notices */}
      {error && (
        <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <AlertTriangle className="w-5 h-5 shrink-0 text-red-600 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold">Operation Error</p>
            <p className="mt-0.5">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {successNotice && (
        <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
          <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-600 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold">Fee Schedule Updated</p>
            <p className="mt-0.5">{successNotice}</p>
          </div>
          <button onClick={() => setSuccessNotice(null)} className="text-emerald-500 hover:text-emerald-700">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Top KPI Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-[var(--muted-foreground)]">
              Active Rails
            </span>
            <div className="rounded-lg bg-[var(--primary)]/10 p-2 text-[var(--primary)]">
              <Receipt className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-3xl font-bold tracking-tight text-[var(--foreground)]">
              {summary ? summary.totalRails : '12'}
            </span>
            <span className="text-xs text-[var(--muted-foreground)]">100% centralized</span>
          </div>
        </div>

        <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-[var(--muted-foreground)]">
              Fixed Fee Rails
            </span>
            <div className="rounded-lg bg-emerald-500/10 p-2 text-emerald-600">
              <ShieldCheck className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-3xl font-bold tracking-tight text-[var(--foreground)]">
              {summary ? summary.fixedCount : '8'}
            </span>
            <span className="text-xs text-[var(--muted-foreground)]">PayShap, EFT, ATM Cash</span>
          </div>
        </div>

        <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-[var(--muted-foreground)]">
              Percentage & Hybrid
            </span>
            <div className="rounded-lg bg-blue-500/10 p-2 text-blue-600">
              <Percent className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-3xl font-bold tracking-tight text-[var(--foreground)]">
              {summary ? summary.percentageCount : '3'}
            </span>
            <span className="text-xs text-[var(--muted-foreground)]">MoolahMove, USDC, EasyPay</span>
          </div>
        </div>

        <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-[var(--muted-foreground)]">
              Tiered Rails
            </span>
            <div className="rounded-lg bg-amber-500/10 p-2 text-amber-600">
              <TrendingUp className="w-5 h-5" />
            </div>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-3xl font-bold tracking-tight text-[var(--foreground)]">
              {summary ? summary.tieredCount : '1'}
            </span>
            <span className="text-xs text-[var(--muted-foreground)]">Flash Voucher Top-Up</span>
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="flex flex-col gap-3 rounded-xl border border-[var(--border)] bg-[var(--card)] p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--muted-foreground)]" />
          <input
            type="text"
            placeholder="Search by rail key, description, or supplier..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-[var(--border)] bg-[var(--background)] pl-9 pr-3 py-2 text-sm text-[var(--foreground)] placeholder-[var(--muted-foreground)] focus:border-[var(--primary)] focus:outline-none focus:ring-1 focus:ring-[var(--primary)]"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <select
            value={selectedSupplier}
            onChange={(e) => setSelectedSupplier(e.target.value)}
            className="rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm text-[var(--foreground)] focus:border-[var(--primary)] focus:outline-none focus:ring-1 focus:ring-[var(--primary)]"
          >
            <option value="">All Suppliers</option>
            <option value="SBSA">Standard Bank (SBSA)</option>
            <option value="FLASH">Flash</option>
            <option value="OTT">OTT Mobile</option>
            <option value="EASYPAY">EasyPay</option>
            <option value="VALR">VALR</option>
            <option value="MYMOOLAH">MyMoolah Internal</option>
          </select>

          <select
            value={selectedFeeType}
            onChange={(e) => setSelectedFeeType(e.target.value)}
            className="rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm text-[var(--foreground)] focus:border-[var(--primary)] focus:outline-none focus:ring-1 focus:ring-[var(--primary)]"
          >
            <option value="">All Fee Models</option>
            <option value="fixed">Fixed</option>
            <option value="percentage">Percentage</option>
            <option value="hybrid">Hybrid</option>
            <option value="tiered">Tiered</option>
          </select>

          {(search || selectedSupplier || selectedFeeType) && (
            <button
              onClick={() => {
                setSearch('');
                setSelectedSupplier('');
                setSelectedFeeType('');
              }}
              className="rounded-lg border border-[var(--border)] px-3 py-2 text-sm text-[var(--muted-foreground)] hover:bg-[var(--muted)]"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Main Table */}
      <div className="overflow-hidden rounded-xl border border-[var(--border)] bg-[var(--card)] shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-[var(--border)] bg-[var(--muted)]/50 text-xs font-semibold uppercase tracking-wider text-[var(--muted-foreground)]">
              <tr>
                <th className="px-5 py-3.5">Payment Rail & Description</th>
                <th className="px-4 py-3.5">Supplier</th>
                <th className="px-4 py-3.5">Fee Model</th>
                <th className="px-4 py-3.5 text-right">Customer Fee (Incl)</th>
                <th className="px-4 py-3.5 text-right">Supplier Pass-Through</th>
                <th className="px-4 py-3.5 text-right">MMTP Gross Margin</th>
                <th className="px-4 py-3.5">Effective Date</th>
                <th className="px-5 py-3.5 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border)]">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-[var(--muted-foreground)]">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <RefreshCw className="w-6 h-6 animate-spin text-[var(--primary)]" />
                      <span>Loading active fee schedules...</span>
                    </div>
                  </td>
                </tr>
              ) : fees.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-[var(--muted-foreground)]">
                    No fee schedules match the selected filters.
                  </td>
                </tr>
              ) : (
                fees.map((fee) => (
                  <tr key={fee.feeKey} className="hover:bg-[var(--muted)]/30 transition-colors">
                    <td className="px-5 py-4">
                      <div className="font-semibold text-[var(--foreground)] flex items-center gap-1.5">
                        <Receipt className="w-4 h-4 text-[var(--primary)] shrink-0" />
                        <code>{fee.feeKey}</code>
                      </div>
                      <div className="text-xs text-[var(--muted-foreground)] mt-0.5 line-clamp-2">
                        {fee.description || 'Centralized transaction fee schedule'}
                      </div>
                    </td>

                    <td className="px-4 py-4">
                      <span className="inline-flex items-center rounded-md bg-slate-100 dark:bg-slate-800 px-2.5 py-1 text-xs font-medium text-slate-700 dark:text-slate-300">
                        {fee.supplierCode || 'MMTP'}
                      </span>
                    </td>

                    <td className="px-4 py-4">
                      {feeModelBadge(fee.feeType)}
                    </td>

                    <td className="px-4 py-4 text-right">
                      <span className="font-bold text-[var(--foreground)]">
                        {formatRateSummary(fee)}
                      </span>
                      {fee.feeType === 'fixed' && (
                        <div className="text-[11px] text-[var(--muted-foreground)]">
                          ex-VAT: {formatZAR(fee.total.exVat)}
                        </div>
                      )}
                      {fee.feeType === 'tiered' && feeHasVolumeBands(fee) && (
                        <div className="text-[11px] text-[var(--muted-foreground)]">
                          Bank cost by monthly count
                        </div>
                      )}
                    </td>

                    <td className="px-4 py-4 text-right">
                      {fee.feeType === 'tiered' && feeHasVolumeBands(fee) ? (
                        <div className="text-xs font-medium text-[var(--muted-foreground)]">See bands</div>
                      ) : (
                        <>
                          <div className="font-medium text-[var(--foreground)]">
                            {fee.feeType === 'percentage'
                              ? formatPercent(fee.supplierPercentageFee * 100)
                              : formatZAR(fee.supplierCostIncl)}
                          </div>
                          <div className="text-[11px] text-[var(--muted-foreground)]">
                            VAT: {fee.feeType === 'percentage' ? 'pass-through' : formatZAR(fee.supplierCost.vat)}
                          </div>
                        </>
                      )}
                    </td>

                    <td className="px-4 py-4 text-right">
                      {fee.feeType === 'tiered' && feeHasVolumeBands(fee) ? (
                        <div className="text-xs font-medium text-[var(--muted-foreground)]">See bands</div>
                      ) : (
                        <>
                          <div className="font-semibold text-emerald-600 dark:text-emerald-400">
                            {fee.feeType === 'percentage'
                              ? formatPercent(fee.mmPercentageFee * 100)
                              : formatZAR(fee.mmMarkupIncl)}
                          </div>
                          <div className="text-[11px] text-[var(--muted-foreground)]">
                            Net: {fee.feeType === 'percentage' ? 'calculated' : formatZAR(fee.mmMarkup.exVat)}
                          </div>
                        </>
                      )}
                    </td>

                    <td className="px-4 py-4">
                      <div className="flex items-center gap-1.5 text-xs text-[var(--foreground)]">
                        <Calendar className="w-3.5 h-3.5 text-[var(--muted-foreground)]" />
                        <span>{fee.effectiveFrom}</span>
                      </div>
                      <div className="text-[11px] text-emerald-600 dark:text-emerald-400 font-medium mt-0.5">
                        {fee.isLive === false ? 'Not yet live' : 'Live'}
                      </div>
                      {fee.upcoming && (
                        <div className="mt-1 inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800 ring-1 ring-amber-200">
                          Upcoming {fee.upcoming.effectiveFrom}
                        </div>
                      )}
                    </td>

                    <td className="px-5 py-4 text-center">
                      <div className="flex items-center justify-center gap-1">
                        {(isAdmin || feeHasVolumeBands(fee) || feeHasVolumeBands(fee.upcoming)) && (
                          <button
                            onClick={() => handleOpenEdit(fee)}
                            title={scheduleLocksVolumeEdit(fee) ? 'View volume bands' : 'Edit Rate (Effective-Dated)'}
                            className="inline-flex items-center gap-1 rounded-lg border border-[var(--border)] px-2.5 py-1.5 text-xs font-medium text-[var(--foreground)] hover:bg-[var(--primary)]/10 hover:text-[var(--primary)] hover:border-[var(--primary)] transition-all"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                            <span>{scheduleLocksVolumeEdit(fee) ? 'View' : 'Edit'}</span>
                          </button>
                        )}
                        <button
                          onClick={() => handleOpenHistory(fee.feeKey)}
                          title="View Version & Audit History"
                          className="inline-flex items-center gap-1 rounded-lg border border-[var(--border)] px-2.5 py-1.5 text-xs font-medium text-[var(--muted-foreground)] hover:bg-[var(--muted)] hover:text-[var(--foreground)] transition-all"
                        >
                          <History className="w-3.5 h-3.5" />
                          <span>History</span>
                        </button>
                        {!scheduleLocksVolumeEdit(fee) && (
                          <button
                            onClick={() => handleOpenSimulate(fee)}
                            title="Simulate Quote Calculation"
                            className="inline-flex items-center gap-1 rounded-lg border border-[var(--border)] px-2.5 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 border-blue-200 transition-all"
                          >
                            <Calculator className="w-3.5 h-3.5" />
                            <span>Test</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* EDIT DRAWER (Effective-Dated Replacement) */}
      {editingFee && (
        <div className="fixed inset-0 z-50 overflow-hidden bg-black/40 backdrop-blur-sm flex justify-end">
          <div className="w-full max-w-2xl bg-[var(--card)] h-full overflow-y-auto p-6 sm:p-8 shadow-2xl border-l border-[var(--border)] flex flex-col justify-between">
            <div>
              {/* Drawer Header */}
              <div className="flex items-start justify-between border-b border-[var(--border)] pb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="rounded-lg bg-[var(--primary)]/10 p-2 text-[var(--primary)]">
                      <Edit3 className="w-5 h-5" />
                    </span>
                    <h2 className="text-xl font-bold text-[var(--foreground)]">
                      {volumeLocked ? 'Fee Schedule' : 'Update Fee Schedule'}
                    </h2>
                  </div>
                  <p className="text-xs text-[var(--muted-foreground)] mt-1">
                    Rail: <code className="font-semibold text-[var(--foreground)]">{editingFee.feeKey}</code> ({editingFee.supplierCode || 'MMTP'})
                  </p>
                </div>
                <button
                  onClick={() => setEditingFee(null)}
                  className="rounded-lg p-2 text-[var(--muted-foreground)] hover:bg-[var(--muted)]"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Form */}
              <form id="fee-edit-form" onSubmit={handleRequestSave} className="space-y-5 mt-6">
                {drawerError && (
                  <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                    <AlertTriangle className="w-5 h-5 shrink-0 text-red-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-semibold">Could not save</p>
                      <p className="mt-0.5">{drawerError}</p>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-[var(--foreground)] mb-1">
                      Fee Model
                    </label>
                    <select
                      value={editForm.feeType}
                      disabled={volumeLocked}
                      onChange={(e) => setEditForm({ ...editForm, feeType: e.target.value as any })}
                      className="w-full rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm text-[var(--foreground)] disabled:opacity-70"
                    >
                      <option value="fixed">Fixed Flat Fee</option>
                      <option value="percentage">Percentage Rate</option>
                      <option value="hybrid">Hybrid (Fixed + %)</option>
                      <option value="tiered">Tiered Schedule</option>
                    </select>
                    {volumeLocked && (
                      <span className="text-[11px] text-[var(--muted-foreground)]">
                        This model is stored with the volume bands. Changing it here would not change the charge.
                      </span>
                    )}
                  </div>

                  {!volumeLocked && (
                  <div>
                    <label className="block text-xs font-semibold text-[var(--foreground)] mb-1">
                      New Effective From Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      required
                      value={editForm.newEffectiveFrom}
                      onChange={(e) => setEditForm({ ...editForm, newEffectiveFrom: e.target.value })}
                      className="w-full rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm text-[var(--foreground)]"
                    />
                    <span className="text-[11px] text-[var(--muted-foreground)]">
                      Default is today. A future date schedules the rate; the current live rate stays in force until then.
                    </span>
                  </div>
                  )}
                </div>

                {volumeLocked && (
                  <div className="space-y-4">
                    <p className="text-sm text-[var(--foreground)]">
                      The payment amount does not set this fee. A sample principal such as R500 does not select a band. The band is this rail&apos;s successful count for the month.
                    </p>
                    {feeHasVolumeBands(editingFee) && (
                      <VolumeBandPanel fee={editingFee} heading={editingFee.isLive === false ? 'Scheduled card' : 'Live card'} />
                    )}
                    {editingFee.upcoming && feeHasVolumeBands(editingFee.upcoming) && (
                      <VolumeBandPanel
                        fee={editingFee.upcoming}
                        heading={`Scheduled card from ${editingFee.upcoming.effectiveFrom}`}
                      />
                    )}
                  </div>
                )}

                {/* Fixed Fees Inputs */}
                {!volumeLocked && (editForm.feeType === 'fixed' || editForm.feeType === 'hybrid') && (
                  <div className="rounded-xl border border-[var(--border)] bg-[var(--muted)]/20 p-4 space-y-4">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-[var(--foreground)]">
                      Fixed Fee Amounts (ZAR, VAT Inclusive)
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-[var(--muted-foreground)] mb-1">
                          Supplier Pass-Through Cost (R)
                        </label>
                        <input
                          type="number"
                          step="0.0001"
                          min="0"
                          value={editForm.supplierCostIncl}
                          onChange={(e) => setEditForm({ ...editForm, supplierCostIncl: parseFloat(e.target.value) || 0 })}
                          className="w-full rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm font-semibold"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[var(--muted-foreground)] mb-1">
                          MMTP Net Markup Margin (R)
                        </label>
                        <input
                          type="number"
                          step="0.0001"
                          min="0"
                          value={editForm.mmMarkupIncl}
                          onChange={(e) => setEditForm({ ...editForm, mmMarkupIncl: parseFloat(e.target.value) || 0 })}
                          className="w-full rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm font-semibold text-emerald-600"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Percentage Rates Inputs */}
                {!volumeLocked && (editForm.feeType === 'percentage' || editForm.feeType === 'hybrid') && (
                  <div className="rounded-xl border border-[var(--border)] bg-[var(--muted)]/20 p-4 space-y-4">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-[var(--foreground)]">
                      Percentage Rates (e.g. 0.0500 = 5.00%)
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-[var(--muted-foreground)] mb-1">
                          Supplier Percentage (0.0000)
                        </label>
                        <input
                          type="number"
                          step="0.0001"
                          min="0"
                          max="1"
                          value={editForm.supplierPercentageFee}
                          onChange={(e) => setEditForm({ ...editForm, supplierPercentageFee: parseFloat(e.target.value) || 0 })}
                          className="w-full rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm font-semibold"
                        />
                        <span className="text-[11px] text-[var(--muted-foreground)]">
                          = {formatPercent(editForm.supplierPercentageFee * 100)}
                        </span>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[var(--muted-foreground)] mb-1">
                          MMTP Markup Percentage (0.0000)
                        </label>
                        <input
                          type="number"
                          step="0.0001"
                          min="0"
                          max="1"
                          value={editForm.mmPercentageFee}
                          onChange={(e) => setEditForm({ ...editForm, mmPercentageFee: parseFloat(e.target.value) || 0 })}
                          className="w-full rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm font-semibold text-emerald-600"
                        />
                        <span className="text-[11px] text-[var(--muted-foreground)]">
                          = {formatPercent(editForm.mmPercentageFee * 100)}
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                      <div>
                        <label className="block text-xs font-medium text-[var(--muted-foreground)] mb-1">
                          Min Fee Cap (ZAR, optional)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="No minimum"
                          value={editForm.minFeeIncl}
                          onChange={(e) => setEditForm({ ...editForm, minFeeIncl: e.target.value })}
                          className="w-full rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-[var(--muted-foreground)] mb-1">
                          Max Fee Cap (ZAR, optional)
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="No maximum"
                          value={editForm.maxFeeIncl}
                          onChange={(e) => setEditForm({ ...editForm, maxFeeIncl: e.target.value })}
                          className="w-full rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Audit Reason */}
                {!volumeLocked && (
                <div>
                  <label className="block text-xs font-semibold text-[var(--foreground)] mb-1">
                    Mandatory Reason for Change (Audit Trail) <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    required
                    rows={8}
                    minLength={5}
                    maxLength={FEE_SCHEDULE_REASON_MAX}
                    value={editForm.reason}
                    onChange={(e) => setEditForm({ ...editForm, reason: e.target.value })}
                    placeholder="e.g., Annual interchange rate adjustment agreed with supplier Q3; lowered MM margin to boost volume."
                    className="w-full rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm text-[var(--foreground)]"
                  />
                  <span className="text-[11px] text-[var(--muted-foreground)]">
                    This reason is recorded immutably in <code>fee_schedule_audit</code>
                    {' '}({editForm.reason.length}/{FEE_SCHEDULE_REASON_MAX}).
                  </span>
                </div>
                )}

                {/* Embedded Live Quote Preview */}
                {!volumeLocked && (
                <div className="rounded-xl border border-[var(--primary)]/30 bg-[var(--primary)]/5 p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold uppercase tracking-wider text-[var(--primary)] flex items-center gap-1.5">
                      <Sparkles className="w-4 h-4" /> Live Calculation Preview
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[var(--muted-foreground)]">Sample Principal: R</span>
                      <input
                        type="number"
                        min="1"
                        value={drawerSimAmount}
                        onChange={(e) => setDrawerSimAmount(parseFloat(e.target.value) || 0)}
                        className="w-24 rounded border border-[var(--border)] bg-[var(--background)] px-2 py-0.5 text-xs font-bold"
                      />
                    </div>
                  </div>

                  {drawerSimResult && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1 text-xs">
                      <div className="bg-[var(--card)] p-2.5 rounded-lg border border-[var(--border)]">
                        <span className="text-[10px] text-[var(--muted-foreground)] uppercase">Customer Fee</span>
                        <div className="text-sm font-bold text-[var(--foreground)] mt-0.5">
                          {formatZAR(drawerSimResult.totalFeeIncl)}
                        </div>
                      </div>
                      <div className="bg-[var(--card)] p-2.5 rounded-lg border border-[var(--border)]">
                        <span className="text-[10px] text-[var(--muted-foreground)] uppercase">Customer Debit</span>
                        <div className="text-sm font-bold text-[var(--foreground)] mt-0.5">
                          {formatZAR(drawerSimResult.totalDebit)}
                        </div>
                      </div>
                      <div className="bg-[var(--card)] p-2.5 rounded-lg border border-[var(--border)]">
                        <span className="text-[10px] text-[var(--muted-foreground)] uppercase">Supplier Cost</span>
                        <div className="text-sm font-semibold text-[var(--muted-foreground)] mt-0.5">
                          {formatZAR(drawerSimResult.supplierCostIncl)}
                        </div>
                      </div>
                      <div className="bg-[var(--card)] p-2.5 rounded-lg border border-[var(--border)]">
                        <span className="text-[10px] text-emerald-700 uppercase">MM Net Revenue</span>
                        <div className="text-sm font-bold text-emerald-600 mt-0.5">
                          {formatZAR(drawerSimResult.netRevenue)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                )}
              </form>
            </div>

            {/* Drawer Actions */}
            <div className="border-t border-[var(--border)] pt-4 mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setEditingFee(null)}
                className="rounded-lg border border-[var(--border)] px-4 py-2 text-sm font-medium text-[var(--foreground)] hover:bg-[var(--muted)]"
              >
                {volumeLocked ? 'Close' : 'Cancel'}
              </button>
              {!volumeLocked && (
              <button
                type="submit"
                form="fee-edit-form"
                disabled={savingEdit}
                className="inline-flex items-center gap-2 rounded-lg bg-[var(--primary)] px-5 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90 disabled:opacity-50"
              >
                {savingEdit ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Applying Effective Rate...</span>
                  </>
                ) : (
                  <>
                    <ShieldCheck className="w-4 h-4" />
                    <span>Save & Apply New Rate</span>
                  </>
                )}
              </button>
              )}
            </div>
          </div>
        </div>
      )}

      {confirmOpen && editingFee && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-2xl border border-[var(--border)] bg-[var(--card)] p-6 shadow-2xl">
            <div className="flex items-start gap-3">
              <span className="rounded-lg bg-amber-50 p-2 text-amber-700">
                <ShieldCheck className="w-5 h-5" />
              </span>
              <div>
                <h2 className="text-lg font-bold text-[var(--foreground)]">Confirm rate change</h2>
                <p className="text-sm text-[var(--muted-foreground)] mt-1">
                  Rail <code className="font-semibold">{editingFee.feeKey}</code> will be replaced with an effective-dated row. History is kept.
                </p>
              </div>
            </div>
            <div className="mt-4 space-y-2 rounded-xl border border-[var(--border)] bg-[var(--muted)]/30 p-4 text-sm">
              <div className="flex justify-between gap-4">
                <span className="text-[var(--muted-foreground)]">Current live rate</span>
                <span className="font-semibold">{formatRateSummary(editingFee)}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-[var(--muted-foreground)]">New rate</span>
                <span className="font-semibold text-emerald-700">
                  {editForm.feeType === 'percentage'
                    ? formatPercent(((Number(editForm.supplierPercentageFee) || 0) + (Number(editForm.mmPercentageFee) || 0)) * 100)
                    : editForm.feeType === 'hybrid'
                    ? `${formatZAR((Number(editForm.supplierCostIncl) || 0) + (Number(editForm.mmMarkupIncl) || 0))} + ${formatPercent(((Number(editForm.supplierPercentageFee) || 0) + (Number(editForm.mmPercentageFee) || 0)) * 100)}`
                    : formatZAR((Number(editForm.supplierCostIncl) || 0) + (Number(editForm.mmMarkupIncl) || 0))}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-[var(--muted-foreground)]">Effective from</span>
                <span className="font-semibold">{editForm.newEffectiveFrom}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-[var(--muted-foreground)]">When it applies</span>
                <span className="font-semibold">
                  {editForm.newEffectiveFrom <= localDateIso()
                    ? 'Live immediately'
                    : `Scheduled — current live rate stays until ${editForm.newEffectiveFrom}`}
                </span>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                disabled={savingEdit}
                className="rounded-lg border border-[var(--border)] px-4 py-2 text-sm font-medium"
              >
                Back
              </button>
              <button
                type="button"
                onClick={handleConfirmSave}
                disabled={savingEdit}
                className="inline-flex items-center gap-2 rounded-lg bg-[var(--primary)] px-5 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                {savingEdit ? <RefreshCw className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                <span>{savingEdit ? 'Saving…' : 'Confirm and apply'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {saveSuccess && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-2xl border border-emerald-200 bg-[var(--card)] p-6 shadow-2xl">
            <div className="flex items-start gap-3">
              <span className="rounded-lg bg-emerald-50 p-2 text-emerald-700">
                <CheckCircle2 className="w-5 h-5" />
              </span>
              <div>
                <h2 className="text-lg font-bold text-[var(--foreground)]">Fee schedule saved</h2>
                <p className="text-sm text-[var(--muted-foreground)] mt-1">{saveSuccess.message}</p>
              </div>
            </div>
            <div className="mt-4 space-y-2 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
              <p>
                Rail <code className="font-semibold">{saveSuccess.feeKey}</code> is
                {saveSuccess.liveNow
                  ? ' live now.'
                  : ` scheduled from ${saveSuccess.effectiveFrom}. Today still uses the previous live rate.`}
              </p>
              <p className="text-xs">
                This confirmation is recorded in the audit history. Use History on the row to review versions.
              </p>
            </div>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => setSaveSuccess(null)}
                className="rounded-lg bg-[var(--primary)] px-5 py-2 text-sm font-semibold text-white"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* HISTORY & AUDIT DRAWER */}
      {historyFeeKey && (
        <div className="fixed inset-0 z-50 overflow-hidden bg-black/40 backdrop-blur-sm flex justify-end">
          <div className="w-full max-w-2xl bg-[var(--card)] h-full overflow-y-auto p-6 sm:p-8 shadow-2xl border-l border-[var(--border)] flex flex-col justify-between">
            <div>
              <div className="flex items-start justify-between border-b border-[var(--border)] pb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="rounded-lg bg-blue-500/10 p-2 text-blue-600">
                      <History className="w-5 h-5" />
                    </span>
                    <h2 className="text-xl font-bold text-[var(--foreground)]">
                      Version & Audit History
                    </h2>
                  </div>
                  <p className="text-xs text-[var(--muted-foreground)] mt-1">
                    Rail: <code className="font-semibold text-[var(--foreground)]">{historyFeeKey}</code>
                  </p>
                </div>
                <button
                  onClick={() => setHistoryFeeKey(null)}
                  className="rounded-lg p-2 text-[var(--muted-foreground)] hover:bg-[var(--muted)]"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {loadingHistory ? (
                <div className="py-20 text-center text-[var(--muted-foreground)]">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto text-[var(--primary)] mb-2" />
                  <span>Loading point-in-time audit logs...</span>
                </div>
              ) : historyData ? (
                <div className="space-y-6 mt-6">
                  {/* Versions Timeline */}
                  <div>
                    <h3 className="text-xs font-bold uppercase tracking-wider text-[var(--muted-foreground)] mb-3">
                      Rate Versions ({historyData.history.length})
                    </h3>
                    <div className="space-y-3">
                      {historyData.history.map((ver, idx) => (
                        <div
                          key={ver.id}
                          className={`rounded-xl border p-4 ${
                            idx === 0
                              ? 'border-[var(--primary)] bg-[var(--primary)]/5'
                              : 'border-[var(--border)] bg-[var(--muted)]/20 opacity-80'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold text-[var(--foreground)]">
                                Version #{ver.id}
                              </span>
                              {idx === 0 && (
                                <span className="rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-bold px-2 py-0.5">
                                  Current
                                </span>
                              )}
                              {feeModelBadge(ver.feeType)}
                            </div>
                            <span className="text-xs font-bold text-[var(--foreground)]">
                              Total: {formatRateSummary(ver)}
                            </span>
                          </div>

                          <div className="mt-2 text-xs text-[var(--muted-foreground)] grid grid-cols-2 gap-2">
                            <div>Effective: <span className="font-medium text-[var(--foreground)]">{ver.effectiveFrom}</span> {ver.effectiveTo ? `to ${ver.effectiveTo}` : '→ Present'}</div>
                            <div>Created By: <span className="font-medium text-[var(--foreground)]">{ver.createdBy || 'system'}</span></div>
                            {!feeHasVolumeBands(ver) && (
                              <>
                                <div>Supplier Pass-Through: <span className="font-medium text-[var(--foreground)]">{formatZAR(ver.supplierCostIncl)}</span></div>
                                <div>MM Margin: <span className="font-medium text-emerald-600">{formatZAR(ver.mmMarkupIncl)}</span></div>
                              </>
                            )}
                          </div>
                          {feeHasVolumeBands(ver) && (
                            <div className="mt-3">
                              <VolumeBandPanel fee={ver} heading="Volume bands" />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Audit Change Logs */}
                  <div>
                    <h3 className="text-xs font-bold uppercase tracking-wider text-[var(--muted-foreground)] mb-3">
                      Immutable Audit Trail ({historyData.audits.length} Records)
                    </h3>
                    {historyData.audits.length === 0 ? (
                      <p className="text-xs text-[var(--muted-foreground)]">No changes recorded yet.</p>
                    ) : (
                      <div className="space-y-3">
                        {historyData.audits.map((a) => (
                          <div key={a.id} className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-4 text-xs space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="font-bold text-[var(--foreground)] capitalize flex items-center gap-1.5">
                                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                                Action: {a.action}
                              </span>
                              <span className="text-[11px] text-[var(--muted-foreground)] flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {new Date(a.changedAt).toLocaleString('en-ZA')}
                              </span>
                            </div>
                            <div className="text-[var(--muted-foreground)]">
                              By: <span className="font-semibold text-[var(--foreground)]">{a.changedBy}</span>
                            </div>
                            {a.reason && (
                              <div className="bg-[var(--muted)]/50 p-2.5 rounded-md text-[var(--foreground)] whitespace-pre-wrap">
                                <span className="font-semibold text-[var(--muted-foreground)]">Reason: </span>
                                {a.reason}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
            </div>

            <div className="border-t border-[var(--border)] pt-4 mt-6 flex justify-end">
              <button
                onClick={() => setHistoryFeeKey(null)}
                className="rounded-lg border border-[var(--border)] px-4 py-2 text-sm font-medium text-[var(--foreground)] hover:bg-[var(--muted)]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* STANDALONE SIMULATOR MODAL */}
      {simulatingFee && (
        <div className="fixed inset-0 z-50 overflow-hidden bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-xl bg-[var(--card)] rounded-2xl border border-[var(--border)] shadow-2xl p-6 sm:p-8 space-y-6">
            <div className="flex items-start justify-between border-b border-[var(--border)] pb-4">
              <div>
                <div className="flex items-center gap-2">
                  <span className="rounded-lg bg-blue-500/10 p-2 text-blue-600">
                    <Calculator className="w-5 h-5" />
                  </span>
                  <h2 className="text-xl font-bold text-[var(--foreground)]">
                    Fee & VAT Math Simulator
                  </h2>
                </div>
                <p className="text-xs text-[var(--muted-foreground)] mt-1">
                  Testing rail: <code className="font-semibold text-[var(--foreground)]">{simulatingFee.feeKey}</code>
                </p>
              </div>
              <button
                onClick={() => setSimulatingFee(null)}
                className="rounded-lg p-2 text-[var(--muted-foreground)] hover:bg-[var(--muted)]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Principal Amount Input */}
            <div className="space-y-3">
              <label className="block text-xs font-semibold text-[var(--foreground)]">
                Enter Principal Transaction Amount (ZAR)
              </label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 font-bold text-sm text-[var(--muted-foreground)]">R</span>
                <input
                  type="number"
                  min="1"
                  step="0.01"
                  value={simAmount}
                  onChange={(e) => setSimAmount(parseFloat(e.target.value) || 0)}
                  className="w-full rounded-xl border border-[var(--border)] bg-[var(--background)] pl-8 pr-4 py-2.5 text-base font-bold text-[var(--foreground)] focus:border-[var(--primary)] focus:outline-none focus:ring-1 focus:ring-[var(--primary)]"
                />
              </div>

              {/* Preset Chips */}
              <div className="flex flex-wrap items-center gap-1.5 pt-1">
                <span className="text-[11px] text-[var(--muted-foreground)] mr-1">Quick presets:</span>
                {PRESET_AMOUNTS.map((amt) => (
                  <button
                    key={amt}
                    onClick={() => setSimAmount(amt)}
                    className={`rounded-md px-2.5 py-1 text-xs font-semibold border transition-all ${
                      simAmount === amt
                        ? 'bg-[var(--primary)] text-white border-[var(--primary)]'
                        : 'bg-[var(--muted)]/40 border-[var(--border)] text-[var(--foreground)] hover:bg-[var(--muted)]'
                    }`}
                  >
                    R {amt}
                  </button>
                ))}
              </div>
            </div>

            {/* Live Calculation Output Card */}
            {simResult && (
              <div className="rounded-xl border border-[var(--border)] bg-[var(--muted)]/30 p-5 space-y-4">
                <div className="flex items-center justify-between border-b border-[var(--border)] pb-3">
                  <div>
                    <span className="text-xs font-bold uppercase text-[var(--muted-foreground)]">Customer Total Debit</span>
                    <div className="text-2xl font-extrabold text-[var(--foreground)] mt-0.5">
                      {formatZAR(simResult.totalDebit)}
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-xs font-bold uppercase text-[var(--muted-foreground)]">Total Transaction Fee</span>
                    <div className="text-2xl font-bold text-[var(--primary)] mt-0.5">
                      {formatZAR(simResult.totalFeeIncl)}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="bg-[var(--card)] p-3 rounded-lg border border-[var(--border)] space-y-1">
                    <span className="text-[11px] font-bold text-[var(--muted-foreground)] uppercase">Supplier Pass-Through</span>
                    <div className="text-sm font-bold text-[var(--foreground)]">{formatZAR(simResult.supplierCostIncl)}</div>
                    <div className="text-[11px] text-[var(--muted-foreground)]">
                      ex-VAT: {formatZAR(simResult.supplierCost.exVat)} | VAT: {formatZAR(simResult.supplierCost.vat)}
                    </div>
                  </div>

                  <div className="bg-[var(--card)] p-3 rounded-lg border border-[var(--border)] space-y-1">
                    <span className="text-[11px] font-bold text-emerald-700 uppercase">MMTP Gross Margin</span>
                    <div className="text-sm font-bold text-emerald-600">{formatZAR(simResult.mmMarkupIncl)}</div>
                    <div className="text-[11px] text-[var(--muted-foreground)]">
                      Net Rev: {formatZAR(simResult.netRevenue)} | Output VAT: {formatZAR(simResult.netVatPayable)}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end pt-2">
              <button
                onClick={() => setSimulatingFee(null)}
                className="rounded-lg bg-[var(--foreground)] px-5 py-2 text-sm font-semibold text-[var(--background)] hover:opacity-90"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
